import React, { useState, useRef, useEffect } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { CalendarDays, ArrowRight } from "lucide-react";
import { Paper, ClickAwayListener } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import TextField from "@mui/material/TextField";
import Button from "./Button";

/**
 * CompactDateRangePicker
 *
 * A 48px pill showing the current date range, with a popover for changing it.
 * Consistent with TimeRangeScroll / TimeRangeSelector design tokens.
 *
 * Props:
 *   startDate  {Date}                          – Current start date.
 *   endDate    {Date}                          – Current end date.
 *   onChange   {(start: Date, end: Date) => void} – Called when Apply is clicked.
 *   disabled   {boolean}                       – Default false.
 */
function CompactDateRangePicker({ startDate, endDate, onChange, disabled = false }) {
  const classes = useStyles();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [localStart, setLocalStart] = useState(startDate ?? null);
  const [localEnd, setLocalEnd] = useState(endDate ?? null);

  // Keep local state in sync when props change while popover is closed
  useEffect(() => {
    if (!open) {
      setLocalStart(startDate ?? null);
      setLocalEnd(endDate ?? null);
    }
  }, [startDate, endDate, open]);

  const handleOpen = () => {
    if (disabled) return;
    setLocalStart(startDate ?? null);
    setLocalEnd(endDate ?? null);
    setOpen(true);
  };

  const handleClickAway = () => {
    setOpen(false);
    // discard uncommitted changes
    setLocalStart(startDate ?? null);
    setLocalEnd(endDate ?? null);
  };

  const handleApply = () => {
    if (localStart && localEnd && onChange) {
      onChange(localStart, localEnd);
    }
    setOpen(false);
  };

  const handleCancel = () => {
    setLocalStart(startDate ?? null);
    setLocalEnd(endDate ?? null);
    setOpen(false);
  };

  const isApplyDisabled = !localStart || !localEnd || (localStart > localEnd);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div className={classes.wrapper}>
        {/* Pill */}
        <div
          ref={anchorRef}
          className={clsx(classes.container, {
            [classes.containerOpen]: open,
            [classes.containerDisabled]: disabled,
          })}
          onClick={handleOpen}
          role="button"
          aria-haspopup="true"
          aria-expanded={open}
        >
          <button
            className={classes.iconButton}
            tabIndex={-1}
            disabled={disabled}
            aria-label="Open date range picker"
            onClick={(e) => { e.stopPropagation(); handleOpen(); }}
          >
            <CalendarDays size={16} strokeWidth={1.75} />
          </button>
          <span className={classes.dateLabel}>
            {formatDate(startDate)}
            <ArrowRight size={12} strokeWidth={1.75} className={classes.arrowIcon} />
            {formatDate(endDate)}
          </span>
        </div>

        {/* Popover */}
        {open && (
          <Paper className={classes.popover} elevation={0}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
              <div className={classes.popover__row}>
                <span className={classes.popover__label}>From</span>
                <DatePicker
                  value={localStart}
                  maxDate={localEnd ?? undefined}
                  inputFormat="dd/MM/yyyy"
                  views={["day", "month", "year"]}
                  onChange={(val) => setLocalStart(val)}
                  renderInput={(params) => (
                    <TextField {...params} size="small" className={classes.popover__input} />
                  )}
                />
              </div>
              <div className={classes.popover__row}>
                <span className={classes.popover__label}>To</span>
                <DatePicker
                  value={localEnd}
                  minDate={localStart ?? undefined}
                  inputFormat="dd/MM/yyyy"
                  views={["day", "month", "year"]}
                  onChange={(val) => setLocalEnd(val)}
                  renderInput={(params) => (
                    <TextField {...params} size="small" className={classes.popover__input} />
                  )}
                />
              </div>
            </LocalizationProvider>

            <div className={classes.popover__footer}>
              <Button variant="secondary" size="small" onClick={handleCancel}>Cancel</Button>
              <Button variant="primary" size="small" onClick={handleApply} disabled={isApplyDisabled}>Apply</Button>
            </div>
          </Paper>
        )}
      </div>
    </ClickAwayListener>
  );
}

function formatDate(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default CompactDateRangePicker;

const useStyles = makeStyles(() => ({
  wrapper: {
    position: "relative",
    display: "inline-flex",
    flexDirection: "column",
  },

  // ── Pill ──────────────────────────────────────────────────────────────
  container: {
    height: "48px",
    display: "inline-flex",
    alignItems: "center",
    gap: "0",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
    borderRadius: "12px",
    padding: "4px",
    cursor: "pointer",
    userSelect: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s ease",
    "&:hover": {
      borderColor: "#D0D0D0",
    },
  },
  containerOpen: {
    borderColor: "#D0D0D0",
  },
  containerDisabled: {
    opacity: 0.5,
    pointerEvents: "none",
  },
  iconButton: {
    width: "40px",
    height: "40px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F8F8F8",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    color: "#0C0E29",
    transition: "background-color 0.15s ease",
    lineHeight: 0,
    "&:hover:not(:disabled)": {
      backgroundColor: "#EDEDED",
    },
  },
  dateLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    fontWeight: "normal",
    color: "#0C0E29",
    padding: "0 14px 0 10px",
    whiteSpace: "nowrap",
  },
  arrowIcon: {
    flexShrink: 0,
    color: "#0C0E29",
  },

  // ── Popover ───────────────────────────────────────────────────────────
  popover: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    zIndex: 1300,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "16px",
    minWidth: "280px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
    borderRadius: "12px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
  },
  popover__row: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  popover__label: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "#6B7280",
    width: "32px",
    flexShrink: 0,
  },
  popover__input: {
    flex: 1,
    "& .MuiInputBase-root": {
      fontFamily: "Lato, sans-serif",
      fontSize: "14px",
      borderRadius: "8px",
    },
  },
  popover__footer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "8px",
    marginTop: "4px",
  },
}));
